/**
 * Custom hook to fetch and manage system logo
 *
 * Usage:
 * const { logoUrl, logoMiniUrl, isLoading, refreshLogo } = useSystemLogo();
 */
import { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import logologmedDefault from '../assets/logo-logmed.png';
import logoMiniDefault from '../assets/logo-mini-default.png';

interface SystemConfig {
  nome_sistema: string;
  logo_url: string | null;
  logo_proxy_url: string | null;
  has_custom_logo: boolean;
  logo_mini_url: string | null;
  logo_mini_proxy_url: string | null;
  has_custom_mini_logo: boolean;
}

// Corrige mixed content: converte http:// para https:// quando a página usa HTTPS
const normalizeUrl = (url: string | null): string | null => {
  if (!url) return null;
  if (window.location.protocol === 'https:' && url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }
  return url;
};

// Trocar o arquivo deve invalidar o cache do proxy, sem incluir credenciais SAS.
const versionMiniProxy = (proxy: string, source: string | null): string => {
  if (!source) return proxy;
  try {
    const url = new URL(source);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return proxy;
    return `${proxy}${proxy.includes('?') ? '&' : '?'}v=${encodeURIComponent(url.pathname)}`;
  } catch {
    return proxy;
  }
};

// Função para atualizar o favicon dinamicamente
const updateFavicon = (url: string | null) => {
  const defaultFavicon = '/logo-mini-default.png';
  const faviconUrl = url || defaultFavicon;

  // Atualiza o link favicon existente ou cria um novo
  let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
  if (!favicon) {
    favicon = document.createElement('link');
    favicon.rel = 'icon';
    favicon.type = 'image/png';
    document.head.appendChild(favicon);
  }
  favicon.href = faviconUrl;

  // Também atualiza apple-touch-icon se existir
  const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement | null;
  if (appleTouchIcon) {
    appleTouchIcon.href = faviconUrl;
  }
};

export const useSystemLogo = () => {
  const [logoUrl, setLogoUrl] = useState<string>(''); // Não define logo padrão imediatamente para evitar flash
  const [logoMiniUrl, setLogoMiniUrl] = useState<string>(''); // Logo mini para sidebar colapsado
  const [nomeSistema, setNomeSistema] = useState<string>('AuditLog');
  const [isLoading, setIsLoading] = useState(true);
  const [hasCustomLogo, setHasCustomLogo] = useState(false);
  const [hasCustomMiniLogo, setHasCustomMiniLogo] = useState(false);

  const fetchSystemConfig = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get<SystemConfig>('/config/system');
      const data = response.data;

      // Use custom logo if available — prefer proxy URL to avoid Azure SAS token issues
      if (data.has_custom_logo) {
        const url = data.logo_proxy_url || normalizeUrl(data.logo_url);
        setLogoUrl(url || logologmedDefault);
        setHasCustomLogo(true);
      } else {
        setLogoUrl(logologmedDefault);
        setHasCustomLogo(false);
      }

      // Use custom mini logo if available — prefer proxy URL
      if (data.has_custom_mini_logo) {
        const url = data.logo_mini_proxy_url
          ? versionMiniProxy(data.logo_mini_proxy_url, data.logo_mini_url)
          : normalizeUrl(data.logo_mini_url);
        setLogoMiniUrl(url || logoMiniDefault);
        setHasCustomMiniLogo(true);
        updateFavicon(url || logoMiniDefault);
      } else {
        setLogoMiniUrl(logoMiniDefault);
        setHasCustomMiniLogo(false);
        updateFavicon(logoMiniDefault);
      }

      setNomeSistema(data.nome_sistema || 'AuditLog');
    } catch (error) {
      console.error('Error fetching system config:', error);
      // Fallback to default logos on error
      setLogoUrl(logologmedDefault);
      setLogoMiniUrl(logoMiniDefault);
      setHasCustomLogo(false);
      setHasCustomMiniLogo(false);
      setNomeSistema('AuditLog');
      updateFavicon(logoMiniDefault);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemConfig();
  }, []);

  return {
    logoUrl,
    logoMiniUrl,
    nomeSistema,
    isLoading,
    hasCustomLogo,
    hasCustomMiniLogo,
    refreshLogo: fetchSystemConfig,
  };
};
